import * as THREE from 'three';
import { CONFIG } from '../../config.js';

export const SIGN_PHRASES = [
  'SYSTEM ONLINE',
  'ACCESS DENIED',
  'WAKE UP',
  'CONNECTION LOST',
  'SIMULATION ACTIVE',
  'SIGNAL DETECTED',
  '010101010101',
];

const _accent = `#${CONFIG.matrix.primaryRed.toString(16).padStart(6, '0')}`;

/**
 * Canvas-based neon / billboard text (no photo textures).
 */
export class DigitalSign {
  /**
   * @param {{
   *   width?: number;
   *   height?: number;
   *   phrases?: string[];
   *   seed?: number;
   *   neon?: boolean;
   * }} [options]
   */
  constructor({
    width = 2.4,
    height = 0.7,
    phrases = SIGN_PHRASES,
    seed = 1,
    neon = false,
  } = {}) {
    this.width = width;
    this.height = height;
    this.phrases = phrases;
    this.neon = neon;
    this._seed = seed >>> 0;
    this._phraseIndex = seed % phrases.length;
    this._phraseTimer = 3 + (seed % 5);
    this._time = 0;

    this._disposed = false;
    this._canvas = document.createElement('canvas');
    this._canvas.width = 256;
    this._canvas.height = 64;
    this._ctx = this._canvas.getContext('2d');
    this._texture = new THREE.CanvasTexture(this._canvas);
    this._texture.colorSpace = THREE.SRGBColorSpace;
    this._texture.minFilter = THREE.LinearFilter;

    this._drawPhrase();

    this.mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(width, height),
      new THREE.MeshBasicMaterial({
        map: this._texture,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        color: neon ? 0xff8899 : 0xffffff,
      }),
    );
    this.mesh.name = neon ? 'NeonSign' : 'DigitalSign';
  }

  /** @param {number} deltaSeconds */
  update(deltaSeconds) {
    this._time += deltaSeconds;
    this._phraseTimer -= deltaSeconds;
    if (this._phraseTimer <= 0) {
      this._phraseIndex = (this._phraseIndex + 1) % this.phrases.length;
      this._phraseTimer = 3.5 + (this._hash() % 100) / 25;
      this._drawPhrase();
    }

    const flicker =
      0.82 +
      0.18 * Math.sin(this._time * (this.neon ? 14 : 9) + this._seed) +
      (this._hash() % 100) / 500;
    const glitch =
      Math.sin(this._time * 37 + this._seed * 0.1) > 0.985 ? 0.55 : 1;
    this.mesh.material.opacity = Math.min(1, flicker * glitch);
  }

  dispose() {
    if (this._disposed) return;
    this._disposed = true;
    this.mesh.geometry.dispose();
    this.mesh.material.dispose();
    this._texture.dispose();
  }

  _drawPhrase() {
    const ctx = this._ctx;
    if (!ctx) return;
    const w = this._canvas.width;
    const h = this._canvas.height;
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, w, h);

    ctx.strokeStyle = _accent;
    ctx.lineWidth = 2;
    ctx.strokeRect(4, 4, w - 8, h - 8);

    const phrase = this.phrases[this._phraseIndex];
    ctx.fillStyle = this.neon ? '#ff8899' : _accent;
    ctx.font = '700 28px "Consolas", "Courier New", monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(phrase, w / 2, h / 2);

    if (phrase.includes('0') && phrase.includes('1')) {
      ctx.font = '600 22px "Consolas", monospace';
      ctx.fillText('0 1 0 1 0 1', w / 2, h - 18);
    }

    this._texture.needsUpdate = true;
  }

  _hash() {
    this._seed = (1664525 * this._seed + 1013904223) >>> 0;
    return this._seed;
  }
}
